<template>
  <section v-bind="$attrs" class="container p-2">
    <h1>Sizing Info</h1>
    <form class="grid" @submit.prevent>
      <fieldset
        class="col grid"
        :disabled="isSizingReadonly || isImportedFromLegacy"
      >
        <FormLabel v-slot="{ id }" text="Name" class="col">
          <InputText
            :id="`${id}`"
            ref="sizingNameField"
            class="w-full"
            type="text"
            :class="{ 'p-invalid': sizingForm.V?.value.title?.$invalid }"
            :model-value="sizingForm.data.value.title ?? ''"
            @update:model-value="
              sizingForm.update({
                title: $event
              })
            "
          />
        </FormLabel>

        <FormLabel v-if="!isHps" v-slot="{ id }" text="Well" class="col">
          <Dropdown
            v-if="!isOnline"
            :id="id"
            :disabled="
              isSizingReadonly ||
              isImportedFromLegacy ||
              user.is_koc_customer ||
              user.is_ecopetrol_customer
            "
            class="w-full"
            :required="true"
            :options="wells"
            :dropdown="true"
            :filter="true"
            option-label="well_name"
            option-value="id"
            append-to="body"
            :virtual-scroller-options="{ itemSize: 32.8 }"
            :model-value="sizingForm.data.value.well_id"
            @update:model-value="
              sizingForm.update({
                well_id: $event
              })
            "
          />
          <Dropdown
            v-else
            :id="id"
            class="w-full"
            :disabled="isSizingReadonly || isImportedFromLegacy"
            :options="erpWells?.getERPWellAddresses || []"
            optionLabel="well_name"
            append-to="body"
            :model-value="sizingForm.data.value.well_id"
            @update:model-value="updateWellOnline"
          >
            <template #value="{ value }">
              <span v-if="value">
                {{ selectedWellName }}
              </span>
              <span v-else> [TBD] </span>
            </template>
            <template #header>
              <div class="p-3">
                <icon
                  v-if="fetchingWells"
                  :icon="['far', 'spinner-third']"
                  spin
                />
                <icon v-else :icon="['far', 'search']" class="pi" />
                <InputText
                  v-model="wellSearch"
                  class="ml-2"
                  type="text"
                  placeholder="Search"
                />
              </div>
            </template>
          </Dropdown>
        </FormLabel>

        <FormLabel v-if="isHps" v-slot="{ id }" text="Location" class="col">
          <InputText
            :id="`${id}`"
            class="w-full"
            type="text"
            :disabled="isSizingReadonly || isImportedFromLegacy"
            :model-value="sizingForm.data.value.hps_location ?? ''"
            @update:model-value="
              sizingForm.update({
                hps_location: $event
              })
            "
          />
        </FormLabel>

        <FormLabel v-slot="{ id }" text="Customer" class="col">
          <Dropdown
            v-if="!isOnline"
            :id="id"
            :disabled="
              isSizingReadonly ||
              isImportedFromLegacy ||
              user.is_koc_customer ||
              user.is_ecopetrol_customer
            "
            class="w-full"
            :required="true"
            :options="customers"
            :dropdown="true"
            :filter="true"
            option-label="customer_name"
            option-value="id"
            append-to="body"
            :virtual-scroller-options="{ itemSize: 32.8 }"
            :model-value="sizingForm.data.value.customer_id"
            @update:model-value="
              sizingForm.update({
                customer_id: $event
              })
            "
          />

          <Dropdown
            v-else
            :id="id"
            :disabled="
              isSizingReadonly ||
              isImportedFromLegacy ||
              user.is_koc_customer ||
              user.is_ecopetrol_customer
            "
            class="w-full"
            :options="erpCustomers?.getERPCustomers || []"
            optionLabel="customer_name"
            append-to="body"
            :model-value="sizingForm.data.value.customer_id"
            @update:model-value="updateCustomerOnline"
          >
            <template #value="{ value }">
              <span v-if="value">
                {{ selectedCustomerName }}
              </span>
              <span v-else> [TBD] </span>
            </template>
            <template #header>
              <div class="p-3">
                <icon
                  v-if="fetchingCustomers"
                  :icon="['far', 'spinner-third']"
                  spin
                />
                <icon v-else :icon="['far', 'search']" />
                <InputText
                  v-model="customerSearch"
                  type="text"
                  placeholder="Search"
                  class="ml-2"
                />
              </div>
            </template>
          </Dropdown>
        </FormLabel>

        <FormLabel v-slot="{ id }" text="Sizing Unit System" class="col">
          <Dropdown
            :id="id"
            v-model="sizingForm.data.value.default_display_units"
            :disabled="isSizingReadonly || isImportedFromLegacy"
            class="w-full"
            :class="{
              'p-invalid': sizingForm.V?.value.default_display_units?.$invalid
            }"
            :required="true"
            :dropdown="true"
            :options="availableDisplayUnits"
            option-label="comment"
            option-value="value"
            append-to="body"
          />
        </FormLabel>

        <FormLabel v-slot="{ id }" text="Disposition" class="col">
          <Dropdown
            :id="id"
            v-model="sizingForm.data.value.disposition"
            :disabled="isSizingReadonly || isImportedFromLegacy"
            class="w-full"
            :class="{ 'p-invalid': sizingForm.V?.value.disposition?.$invalid }"
            :required="true"
            :options="sizingDispositions?.sizing_dispositions"
            :dropdown="true"
            option-label="comment"
            option-value="value"
            append-to="body"
          />
        </FormLabel>

        <FormLabel
          v-if="isNewSizing"
          v-slot="{ id }"
          text="Pumping Type"
          class="col"
        >
          <Dropdown
            :id="id"
            v-model="sizingForm.data.value.pumping_type"
            :disabled="isSizingReadonly || isImportedFromLegacy"
            class="w-full"
            :options="sizingPumpingTypes?.sizing_pumping_type"
            :dropdown="true"
            option-label="comment"
            option-value="value"
            append-to="body"
            @update:model-value="handleUpdatePumpingType"
          />
        </FormLabel>
        <Message
          v-if="isHps"
          class="mt-2 ml-2 col-3"
          severity="info"
          :closable="false"
          >HPS currently is in beta</Message
        >
        <FormLabel v-slot="{ id }" text="Notes" class="col-12">
          <input-text
            :id="`${id}`"
            class="w-full"
            type="text"
            :model-value="sizingForm.data.value.notes ?? ''"
            @update:model-value="
              sizingForm.update({
                notes: $event
              })
            "
          />
          <label>Notes</label>
        </FormLabel>
        <div v-if="!user.is_customer" class="col">
          <label class="text-gray-800"> Share With Customer: </label>
          <Checkbox
            :input-id="`share-cust-${sizingForm.data.value.id}`"
            :disabled="
              !isNewScenario && (isSizingReadonly || isImportedFromLegacy)
            "
            class="ml-2"
            :model-value="sizingForm.data.value.share_with_customer"
            binary
            @update:model-value="
              sizingForm.update({
                share_with_customer: $event
              })
            "
          />
        </div>
      </fieldset>
    </form>

    <div class="flex justify-content-between align-items-center">
      <h1>Scenario Info</h1>
      <div class="flex align-items-center">
        <Button
          v-if="!isNewScenario && scenarios?.length"
          label="Delete"
          :disabled="isScenarioReadonly || isImportedFromLegacy"
          class="p-button-danger p-button-outlined mr-2"
          @click="handleDeleteScenario"
        />
      </div>
    </div>
    <form @submit.prevent>
      <fieldset
        class="grid"
        :disabled="
          !isNewScenario && (isScenarioReadonly || isImportedFromLegacy)
        "
      >
        <FormLabel v-slot="{ id }" text="Title" class="col">
          <InputText
            :id="`${id}`"
            :model-value="scenarioForm.data.value?.title ?? ''"
            class="w-full shadow-none"
            :class="{ 'p-invalid': scenarioForm.V?.value.title?.$invalid }"
            type="text"
            :required="true"
            autofocus
            @update:model-value="
              scenarioForm.update({
                title: $event
              })
            "
          />
        </FormLabel>

        <div class="col-12">
          <div class="flex align-items-center mx-list-2">
            <FormLabel v-slot="{ id }" text="Scenario Unit System">
              <Dropdown
                :id="id"
                option-label="comment"
                option-value="value"
                :disabled="isSizingReadonly || isImportedFromLegacy"
                class="w-24rem"
                :required="true"
                :options="availableDisplayUnits"
                :dropdown="true"
                :model-value="scenarioUnitSystem?.value"
                placeholder="Custom"
                append-to="body"
                @update:model-value="handleUpdateScenarioUnitSystem"
              />
            </FormLabel>

            <div>
              <Button
                class="p-button-info"
                label="Apply Unit System From Sizing"
                :disabled="
                  scenarioUnitSystem?.value ===
                  sizingForm.data.value.default_display_units
                "
                @click="handleApplyDefaultUnitSystem"
              />
            </div>
          </div>
        </div>

        <transition name="vertical-slide">
          <Message
            v-if="
              scenarioForm.diff.value?.display_unit_overrides && !isNewScenario
            "
            class="mt-2 ml-2"
            severity="warn"
            :closable="false"
            >Warning: Changing the scenario unit system and saving will reset
            <span class="font-bold">ALL</span> previously saved unit overrides
            in this scenario</Message
          >
        </transition>

        <div class="col-12 my-3 flex align-items-center">
          <div class="mr-2 text-gray-800">Reason:</div>
          <RadioButton
            :model-value="scenarioForm.data.value.reason"
            :disabled="
              !isNewScenario && (isScenarioReadonly || isImportedFromLegacy)
            "
            name="reason"
            value="tender"
            @update:model-value="
              scenarioForm.update({
                reason: $event
              })
            "
          />

          <span class="ml-1 mr-2">Tender</span>
          <RadioButton
            :model-value="scenarioForm.data.value?.reason"
            :disabled="
              !isNewScenario && (isScenarioReadonly || isImportedFromLegacy)
            "
            name="reason"
            value="quote"
            @update:model-value="scenarioForm.data.value.reason = $event"
          />
          <span class="ml-1">Quote</span>
        </div>

        <div class="col-12 my-3">
          <label class="text-gray-800">
            Type:
            {{ pumpingTypeDisplayName }}</label
          >
        </div>

        <div v-if="!isNewScenario" class="col-12 my-3">
          <label class="text-gray-800"> Installed: </label>
          <Checkbox
            :input-id="`inst-${sizingForm.data.value.id}`"
            :disabled="
              !isNewScenario && (isSizingReadonly || isImportedFromLegacy)
            "
            class="ml-2"
            :model-value="
              sizingForm.data.value.installed_scenario_id ===
              scenarioForm.data.value.id
            "
            binary
            @update:model-value="
              sizingForm.update({
                installed_scenario_id: $event
                  ? scenarioForm.data.value.id
                  : scenarioForm.data.value.id ===
                      sizingForm.initialData.value?.installed_scenario_id
                    ? null
                    : sizingForm.initialData.value?.installed_scenario_id
              })
            "
          />
        </div>

        <FormLabel v-slot="{ id }" text="Notes" class="col-12 my-3">
          <Textarea
            :id="`${id}`"
            :model-value="scenarioForm.data.value?.notes ?? ''"
            class="w-full"
            type="textarea"
            :autoResize="true"
            :rows="2"
            @update:model-value="
              scenarioForm.update({
                notes: $event
              })
            "
          />
        </FormLabel>
        <FormLabel
          v-if="!user.is_customer"
          v-slot="{ id }"
          text="Internal Notes"
          class="col-12 my-3"
        >
          <Textarea
            :id="`${id}`"
            :model-value="scenarioForm.data.value?.internal_notes ?? ''"
            class="w-full"
            type="textarea"
            :autoResize="true"
            :rows="2"
            @update:model-value="
              scenarioForm.update({
                internal_notes: $event
              })
            "
          />
        </FormLabel>
      </fieldset>
    </form>
  </section>

  <DeleteScenarioDialog v-model:should-delete="showConfirmDeleteScenario" />
</template>

<script lang="ts" setup>
import type {
  ErpCustomer,
  ErpWellAddress,
  CustomersQuery,
  WellsQuery,
  DisplayUnitsQuery
} from "gql/api";
import {
  useCustomersQuery,
  useWellsQuery,
  useSizingDispositionsQuery,
  useDisplayUnitsQuery,
  useErpCustomersQuery,
  useErpWellAddressesQuery,
  useUpsertCustomerMutation,
  useUpsertWellMutation,
  Display_Units_Enum,
  Tubing_Flow_Options_Enum,
  useSizingPumpingTypesQuery,
  Sizing_Pumping_Type_Enum,
  Override_Options_Enum,
  Hps_Liquid_Type_Enum
} from "gql/api";
import type { SpyglassUser } from "@/composables/auth/useUserInfo";

import useUserInfo, {
  KUWAIT_OIL_COMPANY_ID,
  ECOPETROL_IDS
} from "@/composables/auth/useUserInfo";

import { computed, inject, onActivated, ref } from "vue";
import Textarea from "primevue/textarea";
import Message from "primevue/message";
import FormLabel from "@/components/FormLabel.vue";

import {
  scenariosKey,
  scenarioFormKey,
  sizingFormKey,
  correlationsFormKey,
  parametersFormKey,
  wellPropertiesFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import { useDebounce, useOnline } from "@vueuse/core";
import { useEditPermissions } from "@/composables/auth/editPermissions";
import DeleteScenarioDialog from "@/components/scenarios/DeleteScenarioDialog.vue";
import { loadUnitPreset } from "@/libs/units/presets";
import type { RouteParams, RouteRecordName } from "vue-router";
import { getScenarioUnitSystem } from "@/utils/scenarioUtils";

const isOnline = useOnline();
const user = useUserInfo() as SpyglassUser;

const emit = defineEmits<{
  (e: "scenario-delete-flow"): void;
}>();

const props = defineProps<{
  sizingId: string | null;
  scenarioId: string | null;
  perfevalId: string | null;
  routeName: RouteRecordName | null | undefined;
  routeParams: RouteParams;
}>();

const { data: sizingPumpingTypes } = useSizingPumpingTypesQuery();

const sizingNameField = ref();
onActivated(() => {
  sizingNameField.value?.$el.select();
});

const handleDeleteScenario = () => {
  emit("scenario-delete-flow");
  showConfirmDeleteScenario.value = true;
};

const showConfirmDeleteScenario = ref(false);

const isNewScenario = computed(() => props.scenarioId === "new");

const isNewSizing = computed(() => props.sizingId === "new");

const customerIds = computed(() => {
  if (user.is_customer && user.customers) {
    return user.customers.toString();
  }
  return "";
});

const { isScenarioReadonly, isSizingReadonly, isImportedFromLegacy } =
  useEditPermissions();

const { data: sizingDispositions } = useSizingDispositionsQuery();
const { data: displayUnits } = useDisplayUnitsQuery();
const { data: customersQueryData, executeQuery: fetchCustomers } =
  useCustomersQuery();
const { data: wellsQueryData, executeQuery: fetchWells } = useWellsQuery({
  variables: { withAggregate: false, withSizings: false }
});

const sizingForm = injectStrict(sizingFormKey);
const scenarios = inject(scenariosKey);
const scenarioForm = injectStrict(scenarioFormKey);
const correlationsForm = injectStrict(correlationsFormKey);
const parametersForm = injectStrict(parametersFormKey);
const wellPropertiesForm = injectStrict(wellPropertiesFormKey);

const availableDisplayUnits = computed<DisplayUnitsQuery["display_units"]>(
  () => {
    const units = displayUnits.value?.display_units ?? [];
    if (isHps.value) {
      return units.filter(u =>
        [
          Display_Units_Enum.UsHps,
          Display_Units_Enum.UsHpsGm,
          Display_Units_Enum.InternationalHps,
          Display_Units_Enum.InternationalHpsM3d
        ].includes(u.value as Display_Units_Enum)
      );
    }
    if (!user.is_admin) {
      return units.filter(
        u =>
          ![
            Display_Units_Enum.UsHps,
            Display_Units_Enum.UsHpsGm,
            Display_Units_Enum.InternationalHps,
            Display_Units_Enum.InternationalHpsM3d
          ].includes(u.value as Display_Units_Enum)
      );
    }
    return units;
  }
);

const isHps = computed(() => {
  return sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.Hps;
});

// When changing the unit system on an already created Sizing
// the user must confirm that they want to apply the unit system changes to
// all scenarios, or the currently selected scenario.
const scenarioUnitSystem = computed(() => {
  // Get generic keys from override blob. If none, it's an older scenario and mark it custom
  const matchedUnitSystem = getScenarioUnitSystem(
    displayUnits.value as DisplayUnitsQuery,
    scenarioForm?.data?.value
  );
  if (matchedUnitSystem) {
    // Update the tubing flow if new scenario and unit system is europe geo
    if (
      isNewScenario.value &&
      matchedUnitSystem.id === Display_Units_Enum.EuropeGeothermal
    ) {
      correlationsForm.update({
        tubing_flow: Tubing_Flow_Options_Enum.HagedornBrown
      });
    } else if (
      isNewScenario.value &&
      matchedUnitSystem.id !== Display_Units_Enum.EuropeGeothermal
    ) {
      correlationsForm.update({
        tubing_flow: Tubing_Flow_Options_Enum.BeggsBrill
      });
    }
    return matchedUnitSystem;
  }

  return null;
});

const handleUpdateScenarioUnitSystem = (e: Display_Units_Enum) => {
  const unitOverrides = loadUnitPreset(e);
  // Update the tubing flow if new scenario and unit system is europe geo
  if (isNewScenario.value && e === Display_Units_Enum.EuropeGeothermal) {
    correlationsForm.update({
      tubing_flow: Tubing_Flow_Options_Enum.HagedornBrown
    });
  } else if (isNewScenario.value && e !== Display_Units_Enum.EuropeGeothermal) {
    correlationsForm.update({
      tubing_flow: Tubing_Flow_Options_Enum.BeggsBrill
    });
  }

  scenarioForm.update({
    display_unit_overrides: unitOverrides
  });
};

const handleApplyDefaultUnitSystem = () => {
  const unitOverrides = loadUnitPreset(
    sizingForm.data.value.default_display_units
  );

  scenarioForm.update({
    display_unit_overrides: unitOverrides
  });
};

// When selecting a customer, we automatically set the well
// to TBD/Null because the list of well is limited to the selected
// Customer. Also, when selecting a well, we automatically set
// the correct Customer.
const customers = computed<CustomersQuery["customers"]>(() =>
  [...(customersQueryData.value?.customers ?? [])].sort((a, b) => a.id - b.id)
);

const wells = computed<WellsQuery["wells"]>(() =>
  [...(wellsQueryData.value?.wells ?? [])].sort((a, b) => a.id - b.id)
);

// ERP Customers
// query falcon for the customers from ERP
const customerSearch = ref("");
const { data: erpCustomers, fetching: fetchingCustomers } =
  useErpCustomersQuery({
    variables: useDebounce(
      computed(() => ({
        name: customerSearch.value || " ",
        customer_erp_id: customerIds.value,
        is_customer: user.is_customer
      })),
      800
    )
  });

const { executeMutation: upsertCustomer } = useUpsertCustomerMutation();

// ERP wells
// query falcon for the wells from ERP
const wellSearch = ref("");

const { data: erpWells, fetching: fetchingWells } = useErpWellAddressesQuery({
  variables: useDebounce(
    computed(() => {
      let customerERPId = customerIds.value;
      if (
        sizingForm.data.value.customer?.customer_erp_id ===
        KUWAIT_OIL_COMPANY_ID
      ) {
        customerERPId = KUWAIT_OIL_COMPANY_ID;
      } else if (
        ECOPETROL_IDS.includes(
          sizingForm.data.value.customer?.customer_erp_id || ""
        )
      ) {
        customerERPId = sizingForm.data.value.customer?.customer_erp_id || "";
      }
      return {
        well_name: wellSearch.value || " ",
        customer_erp_id: customerERPId,
        is_customer: user.is_customer
      };
    }),
    800
  )
});

const { executeMutation: upsertWell } = useUpsertWellMutation();

const selectedCustomerName = computed(
  () =>
    customers.value.find(
      customer => customer.id === sizingForm.data.value.customer_id
    )?.customer_name || "[TBD]"
);

const selectedWellName = computed(
  () =>
    wells.value.find(well => well.id === sizingForm.data.value.well_id)
      ?.well_name
);

// Update customer when well is updated
const updateCustomerOnline = async (event: ErpCustomer, well_id?: number) => {
  if (event.customer_name !== "[TBD]") {
    const { data } = await upsertCustomer({
      object: {
        customer_name: event.customer_name,
        customer_erp_id: event.customer_erp_id
      }
    });

    await fetchCustomers();
    await fetchWells();

    sizingForm.update({
      customer_id: data?.insert_customers_one?.id || null,
      well_id: well_id ?? null,
      customer: data?.insert_customers_one
    });
  } else {
    sizingForm.update({
      customer_id: null,
      well_id: null
    });
  }
};

// Update well when customer is updated
const updateWellOnline = async (event: ErpWellAddress) => {
  if (event.well_name !== "[TBD]") {
    const { data } = await upsertWell({
      object: {
        well_name: event.well_name,
        well_erp_id: event.well_erp_id,
        well_uwi: event.well_uwi,
        address_erp_id: event.address_erp_id
      }
    });

    await fetchCustomers();
    await fetchWells();

    sizingForm.update({
      well_id: data?.insert_wells_one?.id || null
    });

    updateCustomerOnline(
      {
        __typename: "ERPCustomer",
        customer_name: event.customer_name,
        customer_erp_id: event.customer_erp_id
      },
      data?.insert_wells_one?.id
    );
  } else {
    sizingForm.update({
      well_id: null,
      customer_id: null
    });
  }
};

const handleUpdatePumpingType = (val: string) => {
  const isHps = val === Sizing_Pumping_Type_Enum.Hps;
  const parametersPatch = {
    has_compression_pump: isHps,
    pump_override_gas_correction_percent: isHps ? 100 : null,
    intake_packer_installed: isHps,
    include_casing_friction_loss: !isHps,
    intake_gas_separation_override_type: isHps
      ? Override_Options_Enum.Disable
      : Override_Options_Enum.Calculate,
    natural_gas_separation_override_type: isHps
      ? Override_Options_Enum.Disable
      : Override_Options_Enum.Calculate
  };
  const wellPropertiesPatch = {
    hps_liquid_type: isHps ? Hps_Liquid_Type_Enum.Multiphase : null
  };
  scenarioForm.update({ pumping_type: val });
  parametersForm.update(parametersPatch);
  wellPropertiesForm.update(wellPropertiesPatch);
  sizingForm.update({
    pumping_type: val,
    default_display_units: isHps
      ? Display_Units_Enum.UsHps
      : Display_Units_Enum.UsOilField,
    // Clear hps_location when switching away from HPS to prevent stale data
    hps_location: isHps ? undefined : null
  });
};

const pumpingTypeDisplayName = computed(() => {
  const displayName = scenarioForm.data.value.pumping_type
    ?.toUpperCase()
    .replaceAll("_", " ");
  if (isHps.value) {
    return `${displayName}`;
  }
  return displayName;
});
</script>

<style lang="scss" scoped>
@media only screen and (max-width: 750px) {
  .align-items-center {
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;
  }
}
</style>
